import {
  AutoClearTimeouts,
  PersistedSettings,
  State,
  ViewFilterConfig,
} from "../reducers/state";
import { Location } from "./location";
import { Order } from "./order";
import { ViewType } from "./view";

// Settings as they come back out of storage. autoClearTimeouts is optional per
// key as well: a blob written before a status was added carries only the
// statuses that version knew about, so LOAD_USER_PREFS merges it key by key.
export type StoredSettings = Partial<
  Omit<PersistedSettings, "autoClearTimeouts">
> & {
  autoClearTimeouts?: Partial<AutoClearTimeouts>;
};

// The subset of state saved per user. Every field is optional: a stored blob may
// have been written by an older version of the app, so nothing can be assumed
// present.
export interface UserPreferences {
  viewType?: ViewType;
  selectedLocation?: Location;
  filters?: ViewFilterConfig;
  pinnedOrders?: Order[];
  settings?: StoredSettings;
}

export type PersistableState = Pick<
  State,
  "viewType" | "selectedLocation" | "filters" | "pinnedOrders" | "settings"
>;

// Settings restored from storage. Anything else found in a stored blob is
// dropped, including tuning constants that older versions persisted — without
// this a stored orderRefetchIntervalInMS pins the poll interval forever and a
// new default never reaches a browser that has already saved preferences.
const RESTORED_SETTING_KEYS = [
  "customerViewType",
  "customerViewTableCount",
  "customerViewFontSize",
  "customerViewOrdersPerTable",
  "autoClearTimeouts",
  "sortType",
] as const;

const RESTORED_KEYS = [
  "viewType",
  "selectedLocation",
  "filters",
  "pinnedOrders",
] as const;

// Absent keys are omitted rather than set to undefined, so that merging the
// result over the defaults leaves anything missing at its default.
function pickDefined<T, K extends keyof T>(
  source: T | undefined,
  keys: readonly K[]
): Partial<Pick<T, K>> {
  const picked: Partial<Pick<T, K>> = {};

  if (source == null) {
    return picked;
  }

  keys.forEach((key) => {
    if (source[key] !== undefined) {
      picked[key] = source[key];
    }
  });

  return picked;
}

export function toUserPreferences(state: PersistableState): UserPreferences {
  return {
    ...pickDefined(state, RESTORED_KEYS),
    settings: pickDefined(state.settings, RESTORED_SETTING_KEYS),
  };
}

export function parseUserPreferences(
  json: string | null
): UserPreferences | null {
  if (json == null) {
    return null;
  }

  let stored: UserPreferences;

  try {
    stored = JSON.parse(json);
  } catch {
    // A corrupt blob must not break login; fall back to the defaults.
    return null;
  }

  if (stored == null || typeof stored !== "object") {
    return null;
  }

  return {
    ...pickDefined(stored, RESTORED_KEYS),
    settings: pickDefined(stored.settings, RESTORED_SETTING_KEYS),
  };
}
